import { useState } from 'react'
import { motion } from 'motion/react'
import { ArrowUpRightIcon, GlobeIcon, Loader2Icon, PlusIcon } from 'lucide-react'
import type {
  MoodboardItem,
  MoodboardLogoCandidate,
  MoodboardPolarity,
  MoodboardRole,
} from '@backend/lib/db/schema/moodboard'
import { useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'
import { classifyUrl } from '@backend/lib/util/classifyUrl'
import { EnrichingIndicator } from './enriching-indicator'
import { ItemInfoBadge } from './item-info-badge'
import { ResizeHandle } from './resize-handle'

type UrlItem = Extract<MoodboardItem, { type: 'url' }>

type Props = {
  item: UrlItem
  allItems: MoodboardItem[]
  onMove: (position: { x: number; y: number }) => Promise<void> | void
  onResize: (size: { w: number; h: number }) => Promise<void> | void
  onMetaChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
    role?: MoodboardRole | undefined
  }) => Promise<void> | void
  // Upload the chosen logo candidate to S3 and spawn it as a file item next to
  // this URL card. Detail page computes the position; this card hands it the pick.
  onAddLogo: (candidate: MoodboardLogoCandidate) => Promise<void> | void
}

export function LinkCard({ item, allItems, onMove, onResize, onMetaChange, onAddLogo }: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const resizeOverride = useCanvasStore((s) => s.resizeOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const size = resizeOverride?.size ?? item.size
  const isSelected = useCanvasStore((s) => s.selectedIds.has(item.id))
  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    linkedItems: allItems,
    onCommit: onMove,
    onSelect: (e) => {
      useCanvasStore.getState().selectFromPointer(item.id, e.shiftKey)
    },
  })

  const host = (() => {
    try {
      return new URL(item.url).hostname.replace(/^www\./, '')
    } catch {
      return item.url
    }
  })()
  const title = item.scrapedTitle || host
  const embed = classifyUrl(item.url)

  // Embeddable URLs (YouTube / Spotify / Pinterest) get a full-bleed iframe body.
  // Figma-style interaction model: the iframe is inert while the card is
  // unselected (clicks select + drag like any other card); selecting the card
  // makes the iframe interactive so the user can play the video etc. The top
  // drag strip stays grabbable regardless so they can move a selected card too.
  if (embed.kind !== 'plain') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.24, ease: SNAP_CURVE }}
        className="group absolute"
        style={{
          left: position.x,
          top: position.y,
          width: size.w,
          height: size.h,
        }}>
        <div
          {...drag}
          className={[
            'bg-card text-foreground ring-border/60 relative flex h-full w-full flex-col overflow-hidden rounded-lg shadow-sm ring-1 transition-shadow',
            isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
            isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
          ].join(' ')}>
          {/* Top strip — always grabbable, even when the iframe is active. Hosts
              the favicon + host + open link. */}
          <div className="border-border/60 bg-card/95 flex shrink-0 items-center gap-2 border-b px-2 py-1.5">
            <FaviconOrFallback host={host} small />
            <p className="text-muted-foreground flex-1 truncate text-[10px] font-medium uppercase tracking-wide">
              {host}
            </p>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onPointerDown={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-foreground inline-flex items-center">
              <ArrowUpRightIcon size={12} />
            </a>
          </div>

          {/* Embed iframe — sandboxed to scripts + same-origin + popups so video
              players work without giving the embed full window access. Referrer
              policy is the modern default (origin only on cross-origin) — YouTube
              and Spotify reject `no-referrer` for some content.
              `pointer-events` toggles with selection state so clicks pass through
              to the drag handler when the card isn't selected yet. */}
          <iframe
            title={title}
            src={embed.embedUrl}
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            style={{ pointerEvents: isSelected ? 'auto' : 'none' }}
            className="bg-background flex-1 border-0"
          />

          <EnrichingIndicator itemId={item.id} />
          <ItemInfoBadge
            polarity={item.polarity}
            annotation={item.annotation}
            onChange={onMetaChange}
            role={item.role}
            roleEditable
            editor={
              item.extractedLogos && item.extractedLogos.length > 0 ? (
                <LogoCandidatesPicker candidates={item.extractedLogos} onPick={onAddLogo} />
              ) : undefined
            }>
            <p className="text-foreground truncate font-medium">{title}</p>
            <p className="mt-1 break-all">{item.url}</p>
            <p className="mt-2 capitalize">{embed.kind} embed</p>
          </ItemInfoBadge>

          <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="group absolute"
      style={{
        left: position.x,
        top: position.y,
        width: size.w,
        height: size.h,
      }}>
      <div
        {...drag}
        className={[
          'bg-card text-foreground ring-border/60 relative flex h-full w-full flex-col overflow-hidden rounded-lg p-3.5 shadow-sm ring-1 transition-shadow',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
          isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
        ].join(' ')}>
        <EnrichingIndicator itemId={item.id} />
        <div className="flex items-start gap-2.5">
          <FaviconOrFallback host={host} />
          <div className="min-w-0 flex-1">
            <p className="text-foreground truncate text-sm font-semibold leading-tight">{title}</p>
            <p className="text-muted-foreground mt-0.5 truncate text-[10px] font-medium uppercase tracking-wide">
              {host}
            </p>
          </div>
        </div>

        <OgImagePreview candidates={item.extractedLogos} />

        {item.extractedColors && item.extractedColors.length > 0 && (
          <div className="mt-2 flex items-center gap-1">
            {item.extractedColors.slice(0, 6).map((hex) => (
              <span
                key={hex}
                className="ring-border/60 h-3.5 w-3.5 rounded-sm ring-1"
                style={{ backgroundColor: hex }}
                title={hex}
              />
            ))}
          </div>
        )}

        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onPointerDown={(e) => e.stopPropagation()}
          className="text-muted-foreground hover:text-foreground mt-2 inline-flex w-fit items-center gap-1 text-xs font-medium">
          Open
          <ArrowUpRightIcon size={11} />
        </a>

        <ItemInfoBadge
          polarity={item.polarity}
          annotation={item.annotation}
          onChange={onMetaChange}
          role={item.role}
          roleEditable
          editor={
            item.extractedLogos && item.extractedLogos.length > 0 ? (
              <LogoCandidatesPicker candidates={item.extractedLogos} onPick={onAddLogo} />
            ) : undefined
          }>
          <p className="text-foreground truncate font-medium">{title}</p>
          <p className="mt-1 break-all">{item.url}</p>
          <p className="mt-2">
            {item.scrapedContent
              ? `Scraped · ${item.scrapedContent.length} chars across ${item.scrapedPages?.length ?? 1} page${(item.scrapedPages?.length ?? 1) === 1 ? '' : 's'}`
              : 'Not yet scraped — runs at next analyze'}
          </p>
          {item.scrapedPages && item.scrapedPages.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {item.scrapedPages.map((p) => (
                <li key={p} className="truncate" title={p}>
                  · {p}
                </li>
              ))}
            </ul>
          )}
          {item.extractedColors && item.extractedColors.length > 0 && (
            <p className="mt-1">
              {item.extractedColors.length} brand color
              {item.extractedColors.length === 1 ? '' : 's'} detected
            </p>
          )}
          {item.extractedLogos && item.extractedLogos.length > 0 && (
            <p className="mt-1">
              {item.extractedLogos.length} logo
              {item.extractedLogos.length === 1 ? '' : 's'} detected
            </p>
          )}
          {item.scrapedContent && (
            <details className="border-border/60 mt-2 border-t pt-2">
              <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-[10px] uppercase tracking-wide">
                Scraped content
              </summary>
              <pre
                onWheel={(e) => e.stopPropagation()}
                className="text-muted-foreground mt-1.5 max-h-64 overflow-y-auto whitespace-pre-wrap break-words text-[10px] leading-relaxed">
                {item.scrapedContent}
              </pre>
            </details>
          )}
        </ItemInfoBadge>

        <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
      </div>
    </motion.div>
  )
}

// Picks the page's social-preview image (the `<meta property="og:image">` tag)
// out of the URL item's extracted-logos list. Playwright's /logo route returns
// og-image as one of the candidate kinds, so we just grep for it.
// Falls back to nothing — the card has empty space below the favicon row in
// that case, which is fine for a link without preview metadata.
function OgImagePreview({ candidates }: { candidates: MoodboardLogoCandidate[] | undefined }) {
  if (!candidates) return <div className="flex-1" />
  const og = candidates.find((c) => c.type === 'og-image')
  if (!og) return <div className="flex-1" />
  const src = og.dataUrl || og.url
  if (!src) return <div className="flex-1" />
  return (
    <div className="bg-muted mt-3 flex-1 overflow-hidden rounded">
      <img
        src={src}
        alt=""
        draggable={false}
        className="h-full w-full object-cover"
        onError={(e) => {
          ;(e.target as HTMLImageElement).style.display = 'none'
        }}
      />
    </div>
  )
}

// Browsable picker for the URL item's extracted logo candidates. Click a tile →
// the candidate is materialised into S3 + dropped onto the canvas next to this
// card. Tracks per-tile pending state so the user can see which one is uploading.
function LogoCandidatesPicker({
  candidates,
  onPick,
}: {
  candidates: MoodboardLogoCandidate[]
  onPick: (candidate: MoodboardLogoCandidate) => Promise<void> | void
}) {
  const [pendingIdx, setPendingIdx] = useState<number | null>(null)

  const handleClick = async (candidate: MoodboardLogoCandidate, idx: number) => {
    if (pendingIdx !== null) return
    setPendingIdx(idx)
    try {
      await onPick(candidate)
    } finally {
      setPendingIdx(null)
    }
  }

  return (
    <div className="grid gap-1.5">
      <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Logos detected</p>
      <div className="grid grid-cols-4 gap-1.5">
        {candidates.map((candidate, idx) => {
          const isPending = pendingIdx === idx
          return (
            <button
              key={`${candidate.source}-${idx}`}
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => void handleClick(candidate, idx)}
              disabled={pendingIdx !== null}
              title={`${candidate.type} · ${candidate.source}`}
              className="bg-background ring-border/60 hover:ring-primary/60 group relative flex aspect-square items-center justify-center overflow-hidden rounded ring-1 transition-shadow disabled:cursor-not-allowed disabled:opacity-50">
              <LogoCandidatePreview candidate={candidate} />
              <div className="bg-background/80 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                {isPending ? (
                  <Loader2Icon size={12} className="animate-spin" />
                ) : (
                  <PlusIcon size={12} />
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function LogoCandidatePreview({ candidate }: { candidate: MoodboardLogoCandidate }) {
  if (candidate.svg) {
    return (
      <div
        className="h-full w-full"
        // SVG markup comes from Playwright's structured extraction — same trust
        // boundary as the page we already scraped. Rendered as raw markup so we
        // can size inline svgs into the tile.
        dangerouslySetInnerHTML={{ __html: candidate.svg }}
      />
    )
  }
  const src = candidate.dataUrl || candidate.url
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      className="h-full w-full object-contain p-1"
      onError={(e) => {
        ;(e.target as HTMLImageElement).style.display = 'none'
      }}
    />
  )
}

function FaviconOrFallback({ host, small = false }: { host: string; small?: boolean }) {
  const tileSize = small ? 'h-4 w-4' : 'h-7 w-7'
  const iconSize = small ? 8 : 14
  const imgSize = small ? 'h-3 w-3' : 'h-5 w-5'
  return (
    <div
      className={`bg-muted relative flex ${tileSize} shrink-0 items-center justify-center overflow-hidden rounded`}>
      <GlobeIcon className="text-muted-foreground absolute" size={iconSize} />
      <img
        src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
        alt=""
        draggable={false}
        className={`relative rounded-sm ${imgSize}`}
        onError={(e) => {
          ;(e.target as HTMLImageElement).style.display = 'none'
        }}
      />
    </div>
  )
}
